import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Hammer } from "lucide-react";

export default function ChantierTypesPage() {
  const [types, setTypes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chantier-types");
      setTypes(data);
      if (selected) {
        const fresh = data.find((t) => t.id === selected.id);
        setSelected(fresh || null);
      } else if (data.length) {
        setSelected(data[0]);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
     
  }, []);

  const createType = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/chantier-types", { name: newName.trim() });
      toast.success("Type créé");
      setNewOpen(false);
      setNewName("");
      await load();
      setSelected(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveSelected = async (patch) => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/chantier-types/${selected.id}`, patch);
      setSelected(data);
      setTypes((prev) => prev.map((t) => (t.id === data.id ? data : t)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeType = async (t) => {
    if (!window.confirm(`Supprimer le type "${t.name}" ?`)) return;
    try {
      await api.delete(`/chantier-types/${t.id}`);
      toast.success("Type supprimé");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateItem = (idx, patch) => {
    const items = selected.checklist_items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    saveSelected({ checklist_items: items });
  };
  const addItem = () => {
    const items = [
      ...(selected.checklist_items || []),
      { label: "Nouvelle étape", description: null },
    ];
    saveSelected({ checklist_items: items });
  };
  const moveItem = (idx, dir) => {
    const items = [...selected.checklist_items];
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    [items[idx], items[j]] = [items[j], items[idx]];
    saveSelected({ checklist_items: items });
  };
  const removeItem = (idx) => {
    const items = selected.checklist_items.filter((_, i) => i !== idx);
    saveSelected({ checklist_items: items });
  };

  return (
    <div className="space-y-6" data-testid="chantier-types-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Types de chantier</h1>
          <p className="text-sm text-slate-400">
            Définissez vos types récurrents et une checklist associée pour cadrer les visites.
          </p>
        </div>
        <Button
          onClick={() => setNewOpen(true)}
          className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
          data-testid="new-type-btn"
        >
          <Plus className="mr-2 h-4 w-4" /> Nouveau type
        </Button>
      </div>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="border-slate-800 bg-slate-900/40 lg:col-span-1">
            <CardContent className="p-2">
              {types.length === 0 ? (
                <p className="p-4 text-sm text-slate-500">Aucun type — ajoutez-en un.</p>
              ) : (
                <ul>
                  {types.map((t) => (
                    <li key={t.id}>
                      <button
                        onClick={() => setSelected(t)}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${
                          selected?.id === t.id
                            ? "bg-[#C4553B]/15 text-[#C4553B]"
                            : "text-slate-300 hover:bg-slate-800/50"
                        }`}
                        data-testid={`type-item-${t.name}`}
                      >
                        <span className="flex items-center gap-2">
                          <Hammer className="h-3.5 w-3.5" />
                          {t.name}
                        </span>
                        <span className="text-xs text-slate-500">
                          {t.checklist_items?.length || 0} étape(s)
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/40 lg:col-span-2">
            <CardContent className="p-6">
              {!selected ? (
                <p className="text-sm text-slate-500">Sélectionnez un type pour l'éditer.</p>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Input
                      value={selected.name}
                      onChange={(e) => setSelected({ ...selected, name: e.target.value })}
                      onBlur={() => saveSelected({ name: selected.name })}
                      className="flex-1 border-slate-700 bg-slate-950/40 text-lg font-semibold"
                      data-testid="type-name-input"
                    />
                    <button
                      onClick={() => removeType(selected)}
                      className="rounded p-2 text-red-400 hover:bg-red-500/10"
                      data-testid="delete-type-btn"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-slate-300">Checklist</Label>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={addItem}
                        className="border-slate-700 bg-slate-800/60 text-slate-200"
                        data-testid="add-checklist-item"
                      >
                        <Plus className="mr-1 h-3 w-3" /> Étape
                      </Button>
                    </div>
                    {(selected.checklist_items || []).length === 0 ? (
                      <p className="text-sm text-slate-500">Aucune étape. Ajoutez-en une.</p>
                    ) : (
                      <div className="space-y-2">
                        {selected.checklist_items.map((it, idx) => (
                          <div
                            key={it.id || idx}
                            className="rounded-lg border border-slate-700 bg-slate-950/40 p-3"
                            data-testid={`checklist-item-${idx}`}
                          >
                            <div className="flex items-start gap-2">
                              <div className="flex flex-1 flex-col gap-1">
                                <Input
                                  value={it.label}
                                  onChange={(e) =>
                                    setSelected({
                                      ...selected,
                                      checklist_items: selected.checklist_items.map((x, i) =>
                                        i === idx ? { ...x, label: e.target.value } : x,
                                      ),
                                    })
                                  }
                                  onBlur={(e) => updateItem(idx, { label: e.target.value })}
                                  placeholder="Intitulé de l'étape"
                                  className="border-slate-700 bg-slate-900 text-sm"
                                />
                                <Textarea
                                  rows={2}
                                  value={it.description || ""}
                                  onChange={(e) =>
                                    setSelected({
                                      ...selected,
                                      checklist_items: selected.checklist_items.map((x, i) =>
                                        i === idx ? { ...x, description: e.target.value } : x,
                                      ),
                                    })
                                  }
                                  onBlur={(e) => updateItem(idx, { description: e.target.value || null })}
                                  placeholder="Description (optionnelle)"
                                  className="border-slate-700 bg-slate-900 text-xs"
                                />
                              </div>
                              <div className="flex flex-col">
                                <button
                                  onClick={() => moveItem(idx, -1)}
                                  className="rounded p-1 text-slate-400 hover:bg-slate-800"
                                >
                                  <ArrowUp className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => moveItem(idx, 1)}
                                  className="rounded p-1 text-slate-400 hover:bg-slate-800"
                                >
                                  <ArrowDown className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => removeItem(idx)}
                                  className="rounded p-1 text-red-400 hover:bg-red-500/10"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle>Nouveau type de chantier</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Nom</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Toiture, Photovoltaïque, PAC…"
              className="border-slate-700 bg-slate-950/40"
              data-testid="new-type-name"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewOpen(false)}
              className="border-slate-700 bg-slate-800 text-slate-200"
            >
              Annuler
            </Button>
            <Button
              onClick={createType}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="create-type-confirm"
            >
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
